import styles from "~/sass/organisms/Article/Article.module.scss";
import Head from "~/components/Meta/Head";
import ArticleAuthor from "~/components/molecules/Article/ArticleAuthor.js";
import Image from "next/legacy/image";
import Script from "next/script";

export default function HouseHistoryArticle({}) {
  const content = () => {
    return (
      <>
        <div className={styles["Article__image"]}>
          <Image
            src={
              "/static/images/articles/house-history-genealogy-cottage--sm.1x.min.webp"
            }
            alt={"Historical Cottage"}
            width={900}
            height={565}
          />
        </div>
        <h1 className={styles["Article__heading"]}>
          House History and Genealogy
        </h1>
        <ArticleAuthor />
        <p>
          Most people have very strong attachments to their homes, but over
          time, they might also stop to consider who else has called the house
          their home. It's natural to wonder about the history of a home,
          especially if the home is older or unusual in style. Sometimes, new
          homeowners hear a story about their home's former residents from
          neighbors and begin to wonder how much of the story is true and what
          other secrets their home holds. Or perhaps the construction of the
          home is unusual or really well done and they'd like to learn more
          about who designed and built their home. Luckily, it's easier than
          ever to research the history of real estate holdings. Homeowners can
          start by compiling what they know or think they know about the house.
          New researchers should be warned, though, that sometimes, they've
          received bad information, and official records can even be wrong. For
          example, tax records might list the year a home was substantially
          renovated as the year it was built.
        </p>
        <h2 className={styles["Article__sub-heading"]}>Internet Resources</h2>
        <ul>
          <li>
            <a
              rel="nofollow"
              href="https://www.bobvila.com/slideshow/7-ways-to-trace-your-home-s-history-48193"
            >
              Seven Ways to Trace Your Home's History
            </a>
            : Information about private property often ends up scattered around
            several different repositories, including libraries and the county
            clerk's office.
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.thisoldhouse.com/21015083/how-to-research-the-history-of-your-house"
            >
              How to Research the History of Your House
            </a>
            : The house itself is usually full of information about when it was
            constructed and what sort of people lived in it during its early
            years.
          </li>
          <li>
            <a rel="nofollow" href="https://www.oldhouses.com/styleguide">
              Guide to Old House Styles and Architecture
            </a>
            : Knowing the dominant house styles from each decade makes it easier
            to approximately date when a house was constructed.
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.antiquehomesmagazine.com/historic-style-guide/"
            >
              Antique Home Style Guide
            </a>
            : Dating the style of a house makes researching it much easier.
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.bbc.co.uk/history/familyhistory/get_started/house_01.shtml"
            >
              Using Written Archives to Discover the History of Your House
            </a>
            : The BBC wrote this guide about using different types of archives
            to discover a home's history.
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.dhr.virginia.gov/pdf_files/HowtoResearchHistoricProperty_2013.pdf"
            >
              How to Research Your Historic{" "}
            </a>
            <a href="https://www.realestateagents.com/va/richmond">Virginia</a>
            <a
              rel="nofollow"
              href="https://www.dhr.virginia.gov/pdf_files/HowtoResearchHistoricProperty_2013.pdf"
            >
              {" "}
              Property
            </a>
            : Virginia is full of historic properties, but these research ideas
            are also applicable to properties across the country.
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://searshomes.org/index.php/2011/06/06/is-my-house-a-sears-house/comment-page-1/"
            >
              Is My House a Sears House?
            </a>{" "}
            Sears kit homes were built across the country from 1908 to 1940.
            Anyone wondering if they own one can use this guide to look for
            clues.
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://inspectapedia.com/Design/Kit_Houses.php"
            >
              Guide to Kit Houses and Homes
            </a>
            : Sears was not the only manufacturer of kit homes in the 20th
            century.
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://city.milwaukee.gov/cityclerk/hpc/Research"
            >
              Researching a Property in{" "}
            </a>
            <a href="https://www.realestateagents.com/wi/milwaukee">
              Milwaukee
            </a>
            : Although written for Milwaukee homeowners, this guide has good
            information for homeowners everywhere.
          </li>
          <li>
            <a rel="nofollow" href="http://www.lustronlocator.com/">
              Lustron Locator
            </a>
            : Lustron homes were prefab homes made of metal that were sold in
            the mid-20th century.
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.kshs.org/research/pdfs/househistorychecklist.pdf"
            >
              House History Checklist
            </a>
            : Archivists compiled this helpful checklist of areas to focus on
            when researching a house's history.
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.pbs.org/opb/historydetectives/technique/building-background/"
            >
              Building Background
            </a>
            : The foundation of a building can be an excellent source of
            information.
          </li>
          <li>
            <a
              rel="nofollow"
              href="http://myhousehistory.net/researching-house-history-a-case-study-part-one/"
            >
              Researching House History: A Case Study
            </a>
            : Learn from one person's example by reading this account of a home
            history research project.
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://lib.guides.umd.edu/c.php?g=326636&amp;p=2193705"
            >
              Researching Historic Houses
            </a>
            : Library guides compile and explain the various kinds of resources
            available when researching old homes.
          </li>
        </ul>
        <h2 className={styles["Article__sub-heading"]}>Search Resources</h2>
        <p>
          Along with learning about the property, many new homeowners wish to
          know more about the family who originally built the home or even those
          who lived there afterward. Genealogy research tools make this process
          easier. Census records, court documents, newspapers, and city
          directories all can work together to paint a vivid picture of the
          people who once called the house their home.
        </p>
        <h3 className={styles["Article__sub-sub-heading"]}>
          Former Owner Research
        </h3>
        <ul>
          <li>
            <a rel="nofollow" href="https://www.uscitydirectories.com/">
              City Directories of the United States of America
            </a>
            : City directories were typically searchable by the name of the head
            of the household but also by address, making them a valuable tool
            when researching a property.
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.youtube.com/watch?v=e78GPVDp8L0"
            >
              Researching Your House: City Directories
            </a>
            : This video explains how to access and use city directories when
            researching a property's former owners.
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://chroniclingamerica.loc.gov/search/titles/"
            >
              American Newspapers
            </a>
            : The Library of Congress hosts this database of information about
            newspapers dating back to the 1600s.
          </li>
          <li>
            <a rel="nofollow" href="https://theancestorhunt.com/yearbooks.html">
              Yearbooks
            </a>
            : Many older yearbooks are available online and can help researchers
            discover information about the former children who once called the
            house home.
          </li>
        </ul>
        <h3 className={styles["Article__sub-sub-heading"]}>
          Property Legal Issues
        </h3>
        <ul>
          <li>
            <a
              rel="nofollow"
              href="http://www.ncsc.org/Information-and-Resources/Browse-by-State/State-Court-Websites.aspx"
            >
              National Center for State Courts
            </a>
            : Often, older properties turn up in a variety of court documents
            over the years.
          </li>
          <li>
            <a
              rel="nofollow"
              href="http://illinois.outfitters.com/genealogy/land/land.html"
            >
              Legal Land Descriptions
            </a>
            : Over time, there have been many different ways to legally obtain
            land, and this can affect where you should look for property
            records.
          </li>
          <li>
            <a
              rel="nofollow"
              href="http://www.investopedia.com/articles/realestate/12/property-deeds-and-real-property.asp?lgl=myfinance-layout"
            >
              Understanding Property Deeds
            </a>
            : A deed is a legal instrument used to prove ownership of property
            and legally record transfers of the home between owners, making
            these documents valuable sources of information.
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.blm.gov/or/gis/geoscience/files/BLMglossary.pdf"
            >
              Glossary of BLM Surveying and Mapping Terms
            </a>
            : The Bureau of Land Management issued this glossary explaining
            various terms used in surveying.
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.ftc.gov/sites/default/files/documents/one-stops/real-estate-competition/realestateglossary.pdf"
            >
              The Real Estate Marketplace Glossary: How to Talk the Talk
            </a>
            : Learn about the terminology found on real estate documents with
            this guide from the Federal Trade Commission.
          </li>
        </ul>
        <h3 className={styles["Article__sub-sub-heading"]}>
          Geography and Maps
        </h3>
        <ul>
          <li>
            <a
              rel="nofollow"
              href="http://alabamamaps.ua.edu/historicalmaps/FireInsurance/1_County%20Index.html"
            >
              Fire Insurance Maps of Alabama
            </a>
            : Fire insurance maps like these can provide information about
            neighborhoods over the years.
          </li>
        </ul>
        <ul>
          <li>
            <a
              rel="nofollow"
              href="https://www.usgs.gov/products/maps/all-maps"
            >
              Geological and Topographical Maps
            </a>
            : The USGS provides current and historical maps with rich
            information about communities.
          </li>
        </ul>
        <h3 className={styles["Article__sub-sub-heading"]}>Census Records</h3>
        <ul>
          <li>
            <a
              rel="nofollow"
              href="https://www.archives.gov/research/census/about"
            >
              About Census Records
            </a>
            : Census records provide information about the people who once lived
            on a property as well as their neighbors and the larger community.
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.census.gov/history/www/genealogy/decennial_census_records/census_records_2.html"
            >
              Census Records
            </a>
            : The 1950 census is the most current census available to
            researchers.
          </li>
        </ul>
        <h3 className={styles["Article__sub-sub-heading"]}>
          Subscription Internet Services
        </h3>
        <ul>
          <li>
            <a
              rel="nofollow"
              href="http://www.accessible-archives.com/collections/american-county-histories/"
            >
              American County Histories
            </a>
            : This is a historical source for information on the county level.
          </li>
          <li>
            <a
              rel="nofollow"
              href="http://www.proquest.com/products-services/pq-hist-news.html"
            >
              ProQuest Digitized Newspaper Archives
            </a>
            : Libraries and universities often have institutional memberships
            available to their patrons to use this large database of historical
            newspapers.
          </li>
          <li>
            <a rel="nofollow" href="https://www.ancestry.com/">
              Ancestry.com
            </a>
            : This popular genealogy site can be used to research the former
            owners of a property.
          </li>
          <li>
            <a rel="nofollow" href="https://www.myheritage.com/research">
              MyHeritage Search
            </a>
            : It's possible to search this site for free, but payment is
            required to view documents.
          </li>
        </ul>
        <h2 className={styles["Article__sub-heading"]}>Historical Societies</h2>
        <p>
          Historical societies and preservation groups have a vested interest in
          the built environment. Anyone interested in the history of their home
          or neighborhood should reach out to their local historical society. So
          much information has been digitized and placed on the Internet, but
          often, smaller historical societies have a wealth of information that
          only exists as paper documents.
        </p>
        <ul>
          <li>
            <a rel="nofollow" href="https://www.docomomo-us.org/">
              Docomomo
            </a>
            : This nonprofit group works to preserve modern homes and buildings.
          </li>
          <li>
            <a rel="nofollow" href="https://slphistory.org/researchahouse/">
              Researching a House
            </a>
            : Title abstracts are one place to find out about a home's history.
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://savingplaces.org/tips-and-tools#.WSiRX8a1v-g"
            >
              Tips and Tools for Saving Spaces
            </a>
            : The National Trust for Historic Preservation has ideas for saving
            historic properties.
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.georgiatrust.org/resources/for-historic-property-owners/"
            >
              Research Your House
            </a>
            : Some historic homes should be submitted to the National Register,
            and this article explains that process.
          </li>
        </ul>
      </>
    );
  };

  return (
    <>
      <Head
        title={
          "House History and Genealogy: Tracing the Roots of Real Estate | RealEstateAgents.com"
        }
        description={
          "We've collected resources to help you identify the history and genealogy of a home."
        }
      />
      <article className={styles["Article"]}>
        <div className={styles["Article__container"]}>{content()}</div>
      </article>
      <Script strategy="beforeInteractive" type="application/ld+json">
        {{
          "@context": "https://schema.org",
          "@type": "Article",
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id":
              "https://www.realestateagents.com/articles/house-history-genealogy",
          },
          headline:
            "House History and Genealogy: Tracing the Roots of Real Estate",
          author: {
            "@type": "Person",
            name: "Amber Martinez",
            jobTitle: "Education Writer",
            sameAs: [
              "https://www.realestateagents.com/articles/author/amber-martinez",
              "https://www.linkedin.com/in/amber-martinez-7096651a/",
            ],
          },
        }}
      </Script>
    </>
  );
}
